
import {filter} from 'rxjs/operators';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { NavigationService } from './shared/services/navigation.service';
import { SideMenuService } from './shared/side-menu/side-menu.service';
import { Router, NavigationEnd, NavigationStart } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LoaderService } from './shared/loader/loader.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styles: []
})
export class AppComponent implements OnInit, OnDestroy {

	currentUrl: string = '';
	versionKey: string = 'current-version';
	hideMenuView = false;
	showRefreshMessage: boolean = false;
	enableRefreshMessage: boolean = false;
	private readonly _destroying$ = new Subject<void>();

	constructor(
		public translate: TranslateService,
		public sideMenu: SideMenuService,
		private router: Router,
		private navigation: NavigationService,
		private httpClient: HttpClient,
		private msalService: MsalService,
		private msalBroadcastService: MsalBroadcastService,
		private loaderService: LoaderService
	) {

		translate.addLangs(['en']);
		// this language will be used as a fallback when a translation isn't found in the current language
		translate.setDefaultLang('en');

		/**
		 * Fetch default language from one of below:
		 * 1- URL
		 * 2- Cookie
		 * 3- Session
		 * 4- User pref.
		 */
		// translate.use('es');

		this.sideMenu.hideMenuView.subscribe(value => {
			this.hideMenuView = value;
		});

		router.events.pipe(
		filter(event => event instanceof NavigationEnd))
		.subscribe((e: NavigationEnd) => {

			navigation.updateNavigationUrl(e);
		});

		router.events.pipe(
		filter(event => event instanceof NavigationStart))
		.subscribe(eve => {
			const ver = localStorage.getItem(this.versionKey);
			const enableRefreshMessage = this.enableRefreshMessage;

			this.httpClient.post('/auth/updateCheck', {ver}, {
				observe: "body"
			}).subscribe((response: any) => {
				if(response.newVersion) {
					localStorage.setItem(this.versionKey, response.newVersion);
					if(ver)
						this.showRefreshMessage = enableRefreshMessage;
				}
				this.enableRefreshMessage = true;
			});
		});

		this.httpClient.get('/auth/getGitData', {
			observe: "body"
		}).subscribe((response: any) => {
			if(Object.keys(response).length > 0)
				console.log("%c"+JSON.stringify(response), "color: red; font-size: x-large");
		});
	}

	ngOnInit(): void {
		// Initialize MSAL and handle redirect responses
		this.initializeMsal();
	}

	ngOnDestroy(): void {
		this._destroying$.next(undefined);
		this._destroying$.complete();
	}

	private initializeMsal(): void {
		// Handle MSAL redirect response immediately on app initialization
		this.msalService.handleRedirectObservable().subscribe({
			next: (result) => {
				if (result) {
					console.log('🔍 MSAL redirect result received:', result);

					if (result.account && result.accessToken) {
						console.log('✅ MSAL authentication successful, making backend verification call');

						// Set a flag to indicate app component is handling the authentication
						localStorage.setItem('msal.app.handling.auth', 'true');

						// Make backend call to verify account and create session
						this.httpClient.post(window.location.origin + '/msalAuth/login', {
							accessToken: result.accessToken,
							account: result.account
						}).subscribe({
							next: (res: any) => {
								console.log('✅ Backend verification successful, redirecting to sites-map');

								// Clear the flag
								localStorage.removeItem('msal.app.handling.auth');

								// Ensure loader is cleared
								this.loaderService.display(false);

								// Clear the URL parameters and redirect to sites-map
								window.history.replaceState({}, document.title, window.location.pathname + '#/sites-map');
								this.router.navigate(['/sites-map']);
							},
							error: (error) => {
								console.error('❌ Backend verification failed:', error);

								// Clear the flag
								localStorage.removeItem('msal.app.handling.auth');

								// Ensure loader is cleared on error
								this.loaderService.display(false);

								// Clear URL parameters and redirect to login on error
								window.history.replaceState({}, document.title, window.location.pathname + '#/login');
								this.router.navigate(['/login']);
							}
						});
					}
				} else {
					// Check if we have authentication parameters in the URL but no result
					const urlParams = new URLSearchParams(window.location.search);
					const hasAuthParams = urlParams.has('code') || urlParams.has('error') || urlParams.has('state');

					if (hasAuthParams) {
						console.log('⚠️ Authentication parameters detected but no MSAL result');

						if (urlParams.has('error')) {
							console.error('❌ MSAL authentication failed:', urlParams.get('error_description'));
							// Clear the URL parameters and redirect to login
							window.history.replaceState({}, document.title, window.location.pathname + '#/login');
							this.router.navigate(['/login']);
						} else {
							// Clear URL parameters to prevent 404
							console.log('🔄 Clearing authentication parameters from URL');
							window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
						}
					} else {
						// FALLBACK: Check if user is already authenticated in MSAL cache
						// This handles the case where the page is reloaded after successful redirect
						const activeAccount = this.msalService.instance.getActiveAccount();
						if (activeAccount) {
							console.log('🔍 User already authenticated in MSAL cache, attempting to get access token');

							// Try to get an access token silently
							this.msalService.acquireTokenSilent({
								scopes: ['openid', 'profile', 'email'],
								account: activeAccount
							}).subscribe({
								next: (tokenResponse) => {
									console.log('✅ Access token acquired from cache, making backend verification call');

									// Set a flag to indicate app component is handling the authentication
									localStorage.setItem('msal.app.handling.auth', 'true');

									// Make backend call to verify account and create session
									this.httpClient.post(window.location.origin + '/msalAuth/login', {
										accessToken: tokenResponse.accessToken,
										account: activeAccount
									}).subscribe({
										next: (res: any) => {
											console.log('✅ Backend verification successful, redirecting to sites-map');

											// Clear the flag
											localStorage.removeItem('msal.app.handling.auth');

											// Ensure loader is cleared
											this.loaderService.display(false);

											// Redirect to sites-map
											window.history.replaceState({}, document.title, window.location.pathname + '#/sites-map');
											this.router.navigate(['/sites-map']);
										},
										error: (error) => {
											console.error('❌ Backend verification failed:', error);

											// Clear the flag
											localStorage.removeItem('msal.app.handling.auth');

											// Ensure loader is cleared on error
											this.loaderService.display(false);

											// Redirect to login on error
											window.history.replaceState({}, document.title, window.location.pathname + '#/login');
											this.router.navigate(['/login']);
										}
									});
								},
								error: (error) => {
									console.error('❌ Failed to acquire access token from cache:', error);
									// User is not fully authenticated, let them proceed to login
								}
							});
						}
					}
				}
		},
		error: (error) => {
				console.error('❌ MSAL redirect handling failed:', error);

				// Ensure loader is cleared on error
				this.loaderService.display(false);

				// Extract error information from the error object
				let errorCode = 'unknown_error';
				let errorMessage = 'An unknown error occurred during authentication';

				if (error && typeof error === 'object') {
					// MSAL error object
					if (error.errorCode) {
						errorCode = error.errorCode;
					}
					if (error.errorMessage) {
						errorMessage = error.errorMessage;
					} else if (error.message) {
						errorMessage = error.message;
					}
				} else if (typeof error === 'string') {
					errorMessage = error;
				}

				console.error('🔴 MSAL Error Details:', {
					errorCode: errorCode,
					errorMessage: errorMessage,
					fullError: error,
					timestamp: new Date().toISOString()
				});

				// Store error information in localStorage so login component can display it
				localStorage.setItem('msal.redirect.error', JSON.stringify({
					errorCode: errorCode,
					errorMessage: errorMessage,
					timestamp: Date.now()
				}));

				// Clear authentication parameters from URL on error
				const urlParams = new URLSearchParams(window.location.search);
				const hasAuthParams = urlParams.has('code') || urlParams.has('error') || urlParams.has('state');

				if (hasAuthParams) {
					console.log('🔄 Clearing authentication parameters after error');
					window.history.replaceState({}, document.title, window.location.pathname + '#/login');
					this.router.navigate(['/login']);
				}
			}
		});
	}
}
